import { useState } from "react";

import {
  useVoidRefillStep,
  useUndoLastRefillStep,
} from "@/lib/api/refill-steps";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { vesselShortLabel } from "@/lib/refill/vessel";
import type { RefillStep } from "@/lib/api/types";

// StepList renders a session's RefillSteps with first-class undo / revise /
// void affordances (PROMPT §5: "the refill flow is non-linear").
//
//   - Undo: voids the most recent non-voided step.
//   - Void: marks a specific step bad-data with a reason.
//   - Revise: navigates to the edit form (not implemented yet; handler stub).
//
// Voided + superseded steps stay visible but dimmed so the user can see
// what happened. Calibration aggregates already exclude them at the
// service layer.

type Props = {
  sessionID: string;
  steps: RefillStep[];
  onRevise?: (step: RefillStep) => void;
};

export function StepList({ sessionID, steps, onRevise }: Props) {
  if (!steps || steps.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No dose or pour steps recorded yet. Captured readings (current,
        pre-pour, post-pour state) are saved on the session; mix and pour doses
        show up here as you record them in the wizard.
      </p>
    );
  }

  return (
    <ol className="space-y-2">
      {steps.map((s) => (
        <StepRow key={s.id} sessionID={sessionID} step={s} onRevise={onRevise} />
      ))}
      <li className="pt-2">
        <UndoLastControl sessionID={sessionID} />
      </li>
    </ol>
  );
}

function StepRow({
  sessionID,
  step,
  onRevise,
}: {
  sessionID: string;
  step: RefillStep;
  onRevise?: (s: RefillStep) => void;
}) {
  const dim = !!step.voided_at || !!step.superseded_by_id;
  const [reason, setReason] = useState("");
  const [confirmVoid, setConfirmVoid] = useState(false);
  const voidMut = useVoidRefillStep(step.id, sessionID);

  return (
    <li
      className={
        "rounded-md border px-3 py-2 text-sm transition-opacity " +
        (dim ? "opacity-50" : "")
      }
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">
          #{step.step_index} {step.phase}
          {step.target && (
            <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 align-middle text-[10px] font-normal text-muted-foreground">
              {vesselShortLabel(step.target)}
            </span>
          )}
          {step.nutrient_id && (
            <span className="ml-1 text-xs text-muted-foreground">
              · nutrient {step.nutrient_id.slice(0, 8)}…
            </span>
          )}
        </span>
        <span className="text-xs text-muted-foreground">
          actor: {step.actor}
          {step.voided_at && " · voided"}
          {step.superseded_by_id && " · superseded"}
        </span>
      </div>
      {step.ml_dosed !== undefined && (
        <p className="text-xs text-muted-foreground">
          dosed {step.ml_dosed} mL · pre pH/EC: {fmt(step.pre_ph)}/
          {fmt(step.pre_ec)} · post: {fmt(step.post_ph)}/{fmt(step.post_ec)}
        </p>
      )}
      {step.notes && (
        <p className="mt-1 text-xs italic text-muted-foreground">{step.notes}</p>
      )}
      {!dim && (
        <div className="mt-2 flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onRevise?.(step)}
            disabled={!onRevise}
          >
            Revise
          </Button>
          {!confirmVoid ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setConfirmVoid(true)}
            >
              Void
            </Button>
          ) : (
            <>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="reason (required)"
                className="h-8 text-xs"
              />
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={!reason || voidMut.isPending}
                onClick={() =>
                  voidMut.mutate(reason, {
                    onSuccess: () => {
                      setConfirmVoid(false);
                      setReason("");
                    },
                  })
                }
              >
                {voidMut.isPending ? "Voiding…" : "Confirm"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setConfirmVoid(false);
                  setReason("");
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      )}
      {voidMut.error && (
        <p className="mt-1 text-xs text-destructive">
          {String((voidMut.error as Error).message)}
        </p>
      )}
    </li>
  );
}

function UndoLastControl({ sessionID }: { sessionID: string }) {
  const undo = useUndoLastRefillStep(sessionID);
  const [reason, setReason] = useState("");
  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2">
      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="undo reason"
        className="h-8 max-w-xs text-xs"
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!reason || undo.isPending}
        onClick={() =>
          undo.mutate(reason, {
            onSuccess: () => setReason(""),
          })
        }
      >
        {undo.isPending ? "Undoing…" : "Undo last"}
      </Button>
    </div>
  );
}

function fmt(v: number | undefined | null) {
  return v === null || v === undefined ? "—" : v.toFixed(2);
}
